import { useMemo } from 'react';
import { Newspaper, Sparkles, AlertTriangle, Info, Database } from 'lucide-react';
import {
  Button,
  Card,
  CardBody,
  EmptyState,
  PageHeader,
  Select,
  Spinner,
  cn,
  type SelectOption,
} from '../../ui';
import { MarkdownView } from '../Knowledge/MarkdownView';
import { EntityRefLinks } from '../common/EntityRefLinks';
import { useDigest } from '../../features/intelligence/useDigest';
import { rangeLabel, formatWhen } from '../../features/intelligence/intelligenceModel';
import type { DigestRange } from '../../features/intelligence/types';

const RANGES: DigestRange[] = ['daily', 'weekly', 'sprint', 'monthly', 'quarterly'];

/**
 * AI Team Digest — pick a reporting window and generate a skimmable digest
 * (Major Progress / Blockers / Goal Health / Important Changes / New Documents
 * / Decisions) from the team's own entities via REAL Bedrock. Retrieval is
 * client-side; every digest lists the exact entities that fed it.
 */
export function DigestContent() {
  const {
    range,
    setRange,
    result,
    loading,
    generating,
    error,
    inRangeCount,
    useKb,
    setUseKb,
    generate,
  } = useDigest();

  const rangeOptions = useMemo<SelectOption[]>(
    () => RANGES.map((r) => ({ value: r, label: rangeLabel(r) })),
    [],
  );

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-end justify-end gap-2">
        <button
          type="button"
          onClick={() => setUseKb(!useKb)}
          disabled={generating}
          title="Augment the digest with knowledge-base passages"
          className={cn(
            'inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition-colors disabled:opacity-50',
            useKb
              ? 'border-accent/40 bg-accent-soft/60 text-zinc-100'
              : 'border-surface-border bg-surface-1 text-zinc-400 hover:text-zinc-200',
          )}
        >
          <Database className="h-3.5 w-3.5" /> KB context
        </button>
        <div className="w-44">
          <Select
            aria-label="Digest range"
            value={range}
            options={rangeOptions}
            onChange={(e) => setRange(e.target.value as DigestRange)}
            disabled={generating}
          />
        </div>
        <Button
          onClick={() => void generate()}
          loading={generating}
          disabled={loading}
          leftIcon={!generating ? <Sparkles className="h-4 w-4" /> : undefined}
        >
          Generate
        </Button>
      </div>

      <div className="mb-5 flex items-start gap-2 rounded-xl border border-surface-border bg-surface-1/60 px-4 py-3 text-sm text-zinc-400">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500" aria-hidden="true" />
        <p>
          {loading
            ? 'Loading team context…'
            : `${inRangeCount} ${inRangeCount === 1 ? 'entity' : 'entities'} fall in the ${rangeLabel(range)} window. The digest is generated by Bedrock from that context only — nothing is invented, and no productivity or ranking metrics are produced.`}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading team context" />
        </div>
      ) : error ? (
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Couldn't load team context"
          description={error}
        />
      ) : !result ? (
        <EmptyState
          icon={<Newspaper className="h-6 w-6" />}
          title="No digest yet"
          description="Pick a range and generate a digest. It reads only your team's stored entities in that window."
        />
      ) : (
        <div className="space-y-5">
          <Card>
            <CardBody>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-surface-border pb-3">
                <h2 className="text-sm font-semibold text-zinc-100">
                  {rangeLabel(result.range)} digest
                </h2>
                <span className="text-xs text-zinc-500">
                  {formatWhen(result.window.fromIso)} → {formatWhen(result.window.toIso)}
                </span>
              </div>
              <MarkdownView markdown={result.markdown} />
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <EntityRefLinks
                refs={result.usedRefs}
                heading={`Sources (${result.usedRefs.length})`}
                emptyLabel="No entities fell in this window — try a wider range."
                grouped
              />
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  );
}

/**
 * AI Team Digest — legacy standalone route. The unified Recap page now hosts
 * this as its "Digest" tab; this wrapper renders the page title +
 * {@link DigestContent}.
 */
export function DigestPage() {
  return (
    <div>
      <PageHeader
        icon={<Newspaper className="h-5 w-5" />}
        title="AI Team Digest"
        description="A skimmable summary of what the team did in a window — grounded in your goals, work, changes, docs, and decisions."
      />
      <DigestContent />
    </div>
  );
}

export default DigestPage;
